import { Sparkles } from 'lucide-react'
import { useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import Markdown from 'react-markdown'
import { toast } from 'sonner'

import { RichTextareaWithSubmit } from '@/components/composer/RichTextareaWithSubmit'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { AttachmentList } from '@/components/upload/AttachmentList'
import { AttachmentPicker } from '@/components/upload/AttachmentPicker'
import { useFileUploads } from '@/hooks/useFileUploads'
import { parseError } from '@/lib/errors'
import { DocumentType } from '@/models/document'
import { type SalesLeadActivity, useDiscardDraft, useSendDraft, useUpdateDraft } from '@/models/salesLead'
import { ActivityAttachments } from './ActivityAttachments'
import { type LeadEmail } from './contactEmails'
import { RecipientSelect } from './RecipientSelect'

interface DraftActivityCardProps {
  leadId: string
  activity: SalesLeadActivity
  emails: LeadEmail[]
}

export function DraftActivityCard({ leadId, activity, emails }: DraftActivityCardProps) {
  const persistedSubject = activity.draftSubject ?? ''
  const persistedBody = activity.draftBody ?? ''
  const [editing, setEditing] = useState(false)
  const [subject, setSubject] = useState(persistedSubject)
  const [body, setBody] = useState(persistedBody)
  const [recipients, setRecipients] = useState<string[]>(emails[0]?.value ? [emails[0].value] : [])
  const [editorKey, setEditorKey] = useState(0)
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([])
  const leadFiles = (activity.draftAttachments ?? []).filter((file) => !removedFileIds.includes(file.id))

  const toggleRecipient = (email: string) => {
    setRecipients((prev) =>
      prev.some((e) => e.toLowerCase() === email.toLowerCase())
        ? prev.filter((e) => e.toLowerCase() !== email.toLowerCase())
        : [...prev, email]
    )
  }

  const uploads = useFileUploads({ documentType: DocumentType.EMAIL_ATTACHMENT })
  const sendDraft = useSendDraft()
  const updateDraft = useUpdateDraft()
  const discardDraft = useDiscardDraft()
  const busy = sendDraft.isPending || updateDraft.isPending || discardDraft.isPending

  const startEditing = () => {
    setSubject(persistedSubject)
    setBody(persistedBody)
    setEditorKey((k) => k + 1)
    setEditing(true)
  }

  const trimmedSubject = subject.trim()
  const trimmedBody = body.trim()
  const canSave = trimmedSubject.length > 0 && trimmedBody.length > 0
  const canSend =
    recipients.length > 0 && persistedSubject.trim().length > 0 && persistedBody.trim().length > 0

  const handleSave = () => {
    if (!canSave) return
    updateDraft.mutate(
      { leadId, activityId: activity.id, content: body, subject },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  const handleCancel = () => {
    setSubject(persistedSubject)
    setBody(persistedBody)
    setEditing(false)
  }

  const handleSend = () => {
    if (!canSend) return
    // Render the markdown body to HTML exactly like the composer
    // (SendEmailForm) so the email ships an HTML alternative. Without it the
    // send is plain-text-only and the recipient's mail client mangles literal
    // `[url](url)` markdown into a broken href.
    const htmlContent = renderToStaticMarkup(<Markdown>{persistedBody}</Markdown>)
    sendDraft.mutate(
      {
        leadId,
        activityId: activity.id,
        subject: persistedSubject,
        content: persistedBody,
        htmlContent,
        to: recipients,
        attachmentDocumentIds: uploads.uploadedIds,
        leadAttachmentIds: leadFiles.map((file) => file.id),
      },
      {
        onSuccess: () => {
          uploads.clearAll()
          toast.success('Email sent')
        },
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  const handleDiscard = () => {
    discardDraft.mutate({ leadId, activityId: activity.id })
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium">Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={busy}
            placeholder="Subject"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">Message</label>
          <RichTextareaWithSubmit
            editorKey={editorKey}
            value={body}
            onValueChange={setBody}
            buttonProps={{ disabled: busy || !canSave, onClick: handleSave, 'aria-label': 'Save' }}
            addonChildren={
              <Button variant="ghost" size="sm" onClick={handleCancel} disabled={busy}>
                Cancel
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardContent>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1 border-blue-300 bg-blue-50 px-2 py-0.5 text-blue-700">
            <Sparkles className="size-3" />
            AI Email Draft
          </Badge>
          <span className="text-muted-foreground text-sm">
            {new Date(activity.createdAt).toLocaleString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-muted-foreground text-sm">To:</span>
          <RecipientSelect
            options={emails}
            selected={recipients}
            onToggle={toggleRecipient}
            disabled={busy}
            className="w-fit max-w-full border-orange-200 bg-orange-50 px-2 text-sm"
          />
        </div>
        {emails.length === 0 && recipients.length === 0 && (
          <p className="mt-1 text-xs text-red-600">No email on file — add a recipient to send.</p>
        )}
        {persistedSubject && <div className="mt-3 text-sm font-medium">{persistedSubject}</div>}
        <MarkdownEditor
          value={persistedBody}
          onChange={() => {}}
          readOnly
          className="mt-2 border-none! bg-transparent!"
          contentEditableClassName="px-0! py-0! min-h-0! border-none!"
        />
        <ActivityAttachments
          attachments={leadFiles}
          onRemove={(id) => setRemovedFileIds((prev) => [...prev, id])}
        />
        {uploads.attachments.length > 0 && (
          <div className="mt-2">
            <AttachmentList
              attachments={uploads.attachments}
              onRemove={(id) => void uploads.removeAttachment(id)}
            />
          </div>
        )}
        <div className="mt-3 flex items-center gap-2">
          <AttachmentPicker onFiles={(files) => void uploads.addFiles(files)} disabled={busy} />
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleDiscard} disabled={busy}>
              Discard
            </Button>
            <Button variant="outline" size="sm" onClick={startEditing} disabled={busy}>
              Edit
            </Button>
            <Button onClick={handleSend} size="sm" disabled={busy || !canSend || uploads.hasUploading}>
              Send
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
